import React, { useState } from "react";
import { toast } from "sonner";
import { Calculator as CalcIcon, ArrowLeftRight, RefreshCw } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { pct } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CURRENCIES } from "@/lib/i18n";

const COST_FIELDS = [
  ["purchase_price", "Preço de compra"], ["shipping", "Frete"], ["fees", "Taxas"],
  ["taxes", "Impostos"], ["commission", "Comissão"], ["other_costs", "Outros custos"], ["sale_price", "Preço de venda"],
];

export default function Calculator() {
  const [form, setForm] = useState(Object.fromEntries(COST_FIELDS.map(([k]) => [k, ""])));
  const [res, setRes] = useState(null);
  // converter
  const [from, setFrom] = useState("USD");
  const [to, setTo] = useState("BRL");
  const [amount, setAmount] = useState("20");
  const [conv, setConv] = useState(null);
  const [loadingConv, setLoadingConv] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const calc = async () => {
    try {
      const payload = Object.fromEntries(COST_FIELDS.map(([k]) => [k, parseFloat(form[k]) || 0]));
      const { data } = await api.post("/calculator", payload);
      setRes(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const doConvert = async () => {
    setLoadingConv(true);
    try {
      const { data } = await api.get("/currency/convert", { params: { from_currency: from, to_currency: to, amount: parseFloat(amount) || 0 } });
      setConv(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoadingConv(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6 animate-float-up">
      {/* Cost & margin */}
      <div className="rounded-xl border border-border bg-card p-6 space-y-4">
        <div className="flex items-center gap-2"><CalcIcon className="h-5 w-5 text-primary" /><h3 className="font-heading font-semibold">Calculadora de custo e margem</h3></div>
        <div className="grid grid-cols-2 gap-3">
          {COST_FIELDS.map(([k, label]) => (
            <div key={k} className="space-y-1.5">
              <Label className="text-xs">{label}</Label>
              <Input type="number" step="0.01" value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`calc-${k}`} />
            </div>
          ))}
        </div>
        <Button onClick={calc} className="w-full" data-testid="calc-run">Calcular</Button>
        {res && (
          <div className="grid grid-cols-2 gap-3 pt-2">
            {[["Custo total", res.cost_total], ["Lucro estimado", res.profit_est]].map(([k, v]) => (
              <div key={k} className="rounded-lg bg-secondary/50 p-3"><p className="text-xs text-muted-foreground">{k}</p><p className="font-mono font-bold text-lg" data-testid={`calc-${k}`}>{Number(v).toFixed(2)}</p></div>
            ))}
            <div className="rounded-lg bg-primary/10 p-3"><p className="text-xs text-muted-foreground">Margem</p><p className="font-mono font-bold text-lg text-primary">{pct(res.margin_pct)}</p></div>
            <div className="rounded-lg bg-primary/10 p-3"><p className="text-xs text-muted-foreground">ROI</p><p className="font-mono font-bold text-lg text-primary">{pct(res.roi_pct)}</p></div>
          </div>
        )}
      </div>

      {/* Converter */}
      <div className="rounded-xl border border-border bg-card p-6 space-y-4">
        <div className="flex items-center gap-2"><ArrowLeftRight className="h-5 w-5 text-primary" /><h3 className="font-heading font-semibold">Conversor multi-moeda</h3></div>
        <div className="space-y-1.5"><Label className="text-xs">Valor</Label><Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="conv-amount" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label className="text-xs">De</Label>
            <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="conv-from">{CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}</select>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Para</Label>
            <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={to} onChange={(e) => setTo(e.target.value)} data-testid="conv-to">{CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}</select>
          </div>
        </div>
        <Button onClick={doConvert} disabled={loadingConv} className="w-full" data-testid="conv-run">
          {loadingConv ? <RefreshCw className="h-4 w-4 animate-spin" /> : <ArrowLeftRight className="h-4 w-4 mr-1" />} Converter
        </Button>
        {conv && (
          <div className="rounded-lg bg-secondary/50 p-4 space-y-1">
            <p className="font-heading text-3xl font-bold" data-testid="conv-result">{conv.result.toLocaleString("pt-BR")} {conv.to}</p>
            <p className="text-sm text-muted-foreground">1 {conv.from} = {conv.rate} {conv.to}</p>
            <p className="text-[11px] text-muted-foreground font-mono">Taxa atualizada: {conv.updated_at ? new Date(conv.updated_at).toLocaleString("pt-BR") : "—"} · {conv.source}</p>
          </div>
        )}
      </div>
    </div>
  );
}
